/**
 * MaaPrint — Public Customer walk-in flow (mobile-first, WhatsApp-simple).
 *
 * Screens (single-page, driven by `step` state):
 *   1. LANDING  — 4 big cards: Camera / Gallery / Documents / Passport
 *   2. QUEUE    — multi-file list with auto-crop + auto-enhance per image,
 *                 reorder / remove, global colour toggle, single-tap UPI pay.
 *   3. PASSPORT — untouched 8-copy 4×6 flow (iter 44).
 *   4. TRACK    — live order status after Razorpay success.
 *
 * IMPORTANT: This page uses only the existing public `/api/shop/{slug}/*`
 * endpoints. Retailer, Super Admin and Windows Connector code is untouched.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import * as pdfjsLib from "pdfjs-dist";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Image as ImageIcon, ArrowLeft, Loader2,
  CheckCircle2, ShieldCheck, QrCode, Camera, X, Trash2,
  Sun, Palette, Sparkles, Clock3, Package, UserSquare,
  ArrowUp, ArrowDown, Plus, ScanSearch, Files, Zap,
  Rows3, Columns3, RotateCw, Scan,
} from "lucide-react";
import ImageEditor from "@/components/ImageEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

// pdf.js worker — use CDN. Version pinned to package.json (4.8.69).
pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs";

// ── Razorpay lazy loader ──────────────────────────────────────────────
function loadRazorpay() {
  return new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

// ── formatting helpers ────────────────────────────────────────────────
const currency = (n) => `₹${Number(n || 0).toFixed(2)}`;
const isImage = (name) => /\.(jpe?g|png|webp)$/i.test(name || "");
const isPdf   = (name) => /\.pdf$/i.test(name || "");
const uid = () => Math.random().toString(36).slice(2, 10);

async function countPdfPages(file) {
  try {
    const buf = await file.arrayBuffer();
    const doc = await pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
    return doc.numPages;
  } catch {
    return 1; // safe fallback — the batch endpoint will correct it before pay
  }
}

// Client-side 90° pixel rotation. Returns a NEW JPEG File; the caller replaces
// the queue item's file+previewUrl so the uploaded blob matches what's shown.
async function rotateImageFile(file, deg) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("image load failed"));
      im.src = url;
    });
    const swap = deg === 90 || deg === 270;
    const canvas = document.createElement("canvas");
    canvas.width = swap ? img.naturalHeight : img.naturalWidth;
    canvas.height = swap ? img.naturalWidth : img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((deg * Math.PI) / 180);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
    return new File([blob], file.name, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/* ═══════════════════════════════════════════════════════════════════ */
/*                        MAIN COMPONENT                                */
/* ═══════════════════════════════════════════════════════════════════ */

export default function CustomerShop() {
  const { slug } = useParams();
  const [step, setStep] = useState("loading"); // loading|landing|queue|passport|track|closed
  const [shop, setShop] = useState(null);
  const [error, setError] = useState(null);

  // Multi-file queue for the standard flow (Camera / Gallery / Documents).
  const [queue, setQueue] = useState([]);            // array of QueueItem
  const [processing, setProcessing] = useState(0);   // # of files currently being enhanced
  const [color, setColor] = useState(false);         // global colour toggle
  const [orientation, setOrientation] = useState("portrait"); // global A4 orientation
  const [manualCrop, setManualCrop] = useState(null); // {file, name} when auto-detect fails
  // Per-item perspective adjust — reopens ImageEditor on an already-queued file.
  const [perspectiveFor, setPerspectiveFor] = useState(null); // {id, file, name}

  // Passport flow reuses the legacy PassportEditor.
  const [passportJob, setPassportJob] = useState(null);
  const [passportUpload, setPassportUpload] = useState(null);

  // Payment + tracking.
  const [paying, setPaying] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);
  const [trackStatus, setTrackStatus] = useState(null);

  // ── Load shop info ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/shop/${slug}`);
        setShop(data);
        setStep("landing");
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load this shop");
        setStep("closed");
      }
    })();
  }, [slug]);

  // ── Track status polling ────────────────────────────────────────────
  useEffect(() => {
    if (step !== "track" || !orderInfo?.order_id) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await publicApi.get(
          `/shop/track/${orderInfo.order_id}?token=${orderInfo.track_token}`);
        if (!stop) setTrackStatus(data);
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [step, orderInfo]);

  // ── Server-side auto-detect + crop + enhance pipeline ───────────────
  const detectAndEnhanceImage = useCallback(async (file) => {
    // 1) detect document corners
    const detectFd = new FormData();
    detectFd.append("file", file);
    let corners = null, detected = false;
    try {
      const { data } = await publicApi.post(`/shop/${slug}/detect-corners`, detectFd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      corners = Array.isArray(data?.corners) ? data.corners : null;
      detected = !!data?.detected;
    } catch {
      detected = false;
    }
    if (!detected) return { detected: false, corners };
    // 2) enhance strictly to detected corners
    const enhFd = new FormData();
    enhFd.append("file", file);
    enhFd.append("mode", "document");
    enhFd.append("corners", JSON.stringify(corners));
    const { data: blob } = await publicApi.post(
      `/shop/${slug}/enhance-preview`, enhFd,
      { headers: { "Content-Type": "multipart/form-data" }, responseType: "blob" },
    );
    const outName = (file.name || "photo").replace(/\.[^.]+$/, "") + "-scan.jpg";
    return { detected: true, corners, file: new File([blob], outName, { type: "image/jpeg" }) };
  }, [slug]);

  // ── Add multiple files to the queue ─────────────────────────────────
  // Each image is auto-scanned; PDFs are counted client-side.
  // If auto-detect fails on an image we pop the manual crop modal for it.
  const addFiles = useCallback(async (fileList) => {
    if (!fileList || !fileList.length) return;
    setStep("queue");
    for (const raw of Array.from(fileList)) {
      if (raw.size > 25 * 1024 * 1024) {
        toast.error(`${raw.name} exceeds 25 MB`);
        continue;
      }
      if (!isPdf(raw.name) && !isImage(raw.name)) {
        toast.error(`${raw.name} — PDF / JPG / PNG only`);
        continue;
      }
      const itemId = uid();
      // Add a placeholder so the UI shows progress immediately.
      setQueue((q) => [...q, {
        id: itemId, kind: isPdf(raw.name) ? "pdf" : "image",
        name: raw.name, file: raw, previewUrl: null,
        pages: null, processing: true, autoEnhanced: false,
      }]);
      setProcessing((n) => n + 1);

      if (isPdf(raw.name)) {
        const pages = await countPdfPages(raw);
        setQueue((q) => q.map((it) => it.id === itemId
          ? { ...it, pages, processing: false, previewUrl: null }
          : it));
      } else {
        try {
          const result = await detectAndEnhanceImage(raw);
          if (result.detected && result.file) {
            const previewUrl = URL.createObjectURL(result.file);
            setQueue((q) => q.map((it) => it.id === itemId
              ? { ...it, file: result.file, previewUrl, pages: 1,
                  processing: false, autoEnhanced: true, name: result.file.name }
              : it));
          } else {
            // Auto-detect failed → open manual crop for THIS file.
            setQueue((q) => q.filter((it) => it.id !== itemId));
            setManualCrop({ file: raw, name: raw.name });
          }
        } catch (e) {
          toast.info(`${raw.name}: enhance skipped — using original`);
          const previewUrl = URL.createObjectURL(raw);
          setQueue((q) => q.map((it) => it.id === itemId
            ? { ...it, previewUrl, pages: 1, processing: false, autoEnhanced: false }
            : it));
        }
      }
      setProcessing((n) => n - 1);
    }
  }, [detectAndEnhanceImage]);

  const removeFromQueue = (id) => {
    setQueue((q) => {
      const item = q.find((it) => it.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return q.filter((it) => it.id !== id);
    });
  };

  const moveInQueue = (id, dir) => {
    setQueue((q) => {
      const i = q.findIndex((it) => it.id === id);
      if (i < 0) return q;
      const j = i + dir;
      if (j < 0 || j >= q.length) return q;
      const next = q.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  // Rotate an image queue item 90° client-side. Uploaded JPEG == what's shown.
  const rotateItem = async (id) => {
    const item = queue.find((it) => it.id === id);
    if (!item || item.kind !== "image" || item.processing) return;
    // mark processing on this item so the UI reflects the rotate call
    setQueue((q) => q.map((it) => it.id === id ? { ...it, processing: true } : it));
    try {
      const rotated = await rotateImageFile(item.file, 90);
      const previewUrl = URL.createObjectURL(rotated);
      setQueue((q) => q.map((it) => {
        if (it.id !== id) return it;
        if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
        return { ...it, file: rotated, previewUrl, processing: false };
      }));
    } catch {
      toast.error("Could not rotate");
      setQueue((q) => q.map((it) => it.id === id ? { ...it, processing: false } : it));
    }
  };

  // Open the manual crop / perspective editor on an already-queued image.
  const openPerspective = (id) => {
    const item = queue.find((it) => it.id === id);
    if (!item || item.kind !== "image") return;
    setPerspectiveFor({ id, file: item.file, name: item.name });
  };

  // User confirmed perspective edits — replace the queue item's file.
  const acceptPerspective = (edited) => {
    const src = perspectiveFor;
    setPerspectiveFor(null);
    if (!edited || !src) return;
    const previewUrl = URL.createObjectURL(edited);
    setQueue((q) => q.map((it) => {
      if (it.id !== src.id) return it;
      if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      return { ...it, file: edited, previewUrl, autoEnhanced: true };
    }));
  };

  // ── Pricing estimate (client-side, PDFs counted via pdf.js) ─────────
  const pricing = shop?.pricing;
  const totalPages = useMemo(
    () => queue.reduce((s, it) => s + (it.pages || 0), 0),
    [queue]
  );
  const estCost = useMemo(() => {
    if (!pricing) return 0;
    const per = color ? pricing.price_color : pricing.price_bw;
    const sub = totalPages * per;
    const gst = sub * (pricing.gst_pct || 0) / 100;
    return Math.round((sub + gst) * 100) / 100;
  }, [pricing, totalPages, color]);

  // ── Confirm manual crop → treat the cropped JPEG as an image item ──
  const acceptManualCrop = (edited) => {
    const src = manualCrop;
    setManualCrop(null);
    if (!edited) return;
    const previewUrl = URL.createObjectURL(edited);
    setQueue((q) => [...q, {
      id: uid(), kind: "image",
      name: (src?.name || "photo").replace(/\.[^.]+$/, "") + "-scan.jpg",
      file: edited, previewUrl, pages: 1, processing: false, autoEnhanced: true,
    }]);
    if (step !== "queue") setStep("queue");
  };

  // ── PAY: upload batch → open UPI-only Razorpay → success → track ────
  const payNow = useCallback(async () => {
    if (!queue.length || paying) return;
    if (processing > 0) {
      toast.info("Still preparing — one moment…");
      return;
    }
    setPaying(true);
    try {
      // 1) Upload batch (server merges into one composite PDF).
      const fd = new FormData();
      for (const it of queue) fd.append("files", it.file, it.name);
      fd.append("copies", JSON.stringify(queue.map(() => 1)));
      fd.append("color", String(color));
      fd.append("duplex", "false");
      fd.append("paper_size", "A4");
      fd.append("orientation", orientation);
      fd.append("enhance_mode", "off"); // images already enhanced client-side
      // crop_corners_list is null-array; server will not re-crop since enhance_mode=off.
      const { data: uploadResp } = await publicApi.post(
        `/shop/${slug}/upload-batch`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      // 2) Create Razorpay order.
      const payFd = new FormData();
      payFd.append("upload_id", uploadResp.upload_id);
      const { data: order } = await publicApi.post(`/shop/${slug}/pay`, payFd);
      if (order.provider !== "razorpay" || !order.key_id?.startsWith("rzp_")) {
        toast.error("Payments not configured on this shop");
        setPaying(false);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Razorpay failed to load");
        setPaying(false);
        return;
      }

      // 3) UPI-only Razorpay (opens UPI apps directly; QR fallback inside modal).
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount_paise, currency: order.currency,
        order_id: order.order_id, name: shop.shop_name,
        description: `Print · ${uploadResp.file_count} file${uploadResp.file_count > 1 ? "s" : ""} · ${uploadResp.total_pages} page${uploadResp.total_pages > 1 ? "s" : ""}`,
        theme: { color: "#0F62FE" },
        config: {
          display: {
            blocks: {
              banks: {
                name: "Pay via UPI",
                instruments: [{ method: "upi", flows: ["intent", "collect", "qr"] }],
              },
            },
            sequence: ["block.banks"],
            preferences: { show_default_blocks: false },
          },
        },
        handler: async (res) => {
          try {
            const { data } = await publicApi.post(`/shop/${slug}/confirm`, {
              upload_id: uploadResp.upload_id,
              order_id: res.razorpay_order_id,
              payment_id: res.razorpay_payment_id,
              signature: res.razorpay_signature,
            });
            setOrderInfo(data);
            setStep("track");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Verification failed");
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", (r) => {
        const err = r?.error || {};
        publicApi.post(`/shop/${slug}/payment-failed`, {
          upload_id: uploadResp.upload_id, order_id: order.order_id,
          error_code: err.code, error_description: err.description,
        }).catch(() => {});
        toast.error(err.description || "Payment failed. Please try again.");
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  }, [queue, color, orientation, slug, shop, paying, processing]);

  // ── Passport picker → dedicated legacy flow ─────────────────────────
  const startPassport = async (file) => {
    if (!file) return;
    if (!isImage(file.name)) return toast.error("Passport photos must be JPG / PNG");
    if (file.size > 25 * 1024 * 1024) return toast.error("Max 25 MB");
    setPassportJob({ file, filename: file.name });
    setPassportUpload(null);
    setStep("passport");
  };

  const resetAll = () => {
    for (const it of queue) if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
    setQueue([]); setPassportJob(null); setPassportUpload(null);
    setOrderInfo(null); setTrackStatus(null);
    setStep("landing");
  };

  // ── Render ─────────────────────────────────────────────────────────
  if (step === "loading") return <FullscreenLoader label="Loading shop…" />;
  if (step === "closed")  return <ClosedShop error={error} />;

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-neutral-50 via-white to-neutral-100 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-900 text-foreground">
      <Toaster position="top-center" richColors closeButton />
      <ShopHeader shop={shop} onBack={step !== "landing" && step !== "track" ? resetAll : null} />

      <main className="mx-auto max-w-md px-4 pb-32 pt-3 space-y-5" data-testid="customer-shop-root">
        {step === "landing" && (
          <Landing
            shop={shop}
            onCamera={(files) => addFiles(files)}
            onGallery={(files) => addFiles(files)}
            onDocuments={(files) => addFiles(files)}
            onPassport={startPassport}
          />
        )}

        {step === "queue" && (
          <QueueScreen
            queue={queue}
            processing={processing}
            color={color} setColor={setColor}
            orientation={orientation} setOrientation={setOrientation}
            estCost={estCost}
            totalPages={totalPages}
            pricing={pricing}
            paying={paying}
            onRemove={removeFromQueue}
            onMove={moveInQueue}
            onRotate={rotateItem}
            onPerspective={openPerspective}
            onAddCamera={(files) => addFiles(files)}
            onAddGallery={(files) => addFiles(files)}
            onAddDocuments={(files) => addFiles(files)}
            onPay={payNow}
            onCancel={resetAll}
          />
        )}

        {step === "passport" && passportJob && (
          <PassportEditor
            job={passportJob} slug={slug}
            upload={passportUpload}
            setUpload={setPassportUpload}
            onContinue={() => payPassportUpload(publicApi, shop, slug, passportUpload, setPaying, setOrderInfo, setStep)}
            paying={paying}
          />
        )}

        {step === "track" && orderInfo && (
          <TrackScreen
            orderInfo={orderInfo}
            trackStatus={trackStatus}
            shop={shop}
            onNew={resetAll}
          />
        )}
      </main>

      {/* Manual 4-corner crop fallback — only when server auto-detect fails. */}
      {manualCrop && (
        <ImageEditor
          open={true}
          src={URL.createObjectURL(manualCrop.file)}
          filename={manualCrop.name}
          client={publicApi}
          renderEndpoint={`/shop/${slug}/editor/render`}
          detectCornersEndpoint={`/shop/${slug}/detect-corners`}
          hideCropTab={true}
          onCancel={() => setManualCrop(null)}
          onConfirm={acceptManualCrop}
        />
      )}

      {/* Per-item perspective adjust — reopens ImageEditor on a queued image. */}
      {perspectiveFor && (
        <ImageEditor
          open={true}
          src={URL.createObjectURL(perspectiveFor.file)}
          filename={perspectiveFor.name}
          client={publicApi}
          renderEndpoint={`/shop/${slug}/editor/render`}
          detectCornersEndpoint={`/shop/${slug}/detect-corners`}
          hideCropTab={true}
          onCancel={() => setPerspectiveFor(null)}
          onConfirm={acceptPerspective}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/*                       PASSPORT PAY HELPER                             */
/* ═══════════════════════════════════════════════════════════════════ */

// Single-tap UPI pay for a prepared passport upload. Kept separate so the
// PassportEditor stays unchanged from iter 44.
async function payPassportUpload(api, shop, slug, upload, setPaying, setOrderInfo, setStep) {
  if (!upload) return;
  setPaying(true);
  try {
    const fd = new FormData();
    fd.append("upload_id", upload.upload_id);
    const { data: order } = await api.post(`/shop/${slug}/pay`, fd);
    if (order.provider !== "razorpay" || !order.key_id?.startsWith("rzp_")) {
      toast.error("Payments not configured on this shop"); setPaying(false); return;
    }
    const ok = await loadRazorpay();
    if (!ok) { toast.error("Razorpay failed to load"); setPaying(false); return; }
    const rzp = new window.Razorpay({
      key: order.key_id, amount: order.amount_paise, currency: order.currency,
      order_id: order.order_id, name: shop.shop_name,
      description: `Passport · 8 copies`,
      theme: { color: "#0F62FE" },
      config: { display: {
        blocks: { banks: { name: "Pay via UPI",
          instruments: [{ method: "upi", flows: ["intent", "collect", "qr"] }] } },
        sequence: ["block.banks"], preferences: { show_default_blocks: false } } },
      handler: async (res) => {
        try {
          const { data } = await api.post(`/shop/${slug}/confirm`, {
            upload_id: upload.upload_id,
            order_id: res.razorpay_order_id,
            payment_id: res.razorpay_payment_id,
            signature: res.razorpay_signature,
          });
          setOrderInfo(data);
          setStep("track");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Verification failed");
        } finally { setPaying(false); }
      },
      modal: { ondismiss: () => setPaying(false) },
    });
    rzp.on("payment.failed", () => setPaying(false));
    rzp.open();
  } catch (e) {
    toast.error(e.response?.data?.detail || "Could not start payment");
    setPaying(false);
  }
}

/* ═══════════════════════════════════════════════════════════════════ */
/*                              SHELL                                    */
/* ═══════════════════════════════════════════════════════════════════ */

function ShopHeader({ shop, onBack }) {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-white/85 dark:bg-neutral-950/85 border-b border-border">
      <div className="mx-auto max-w-md px-4 h-14 flex items-center gap-2">
        {onBack ? (
          <button onClick={onBack} className="h-10 w-10 -ml-2 flex items-center justify-center rounded-full hover:bg-secondary" data-testid="cs-back-btn">
            <ArrowLeft size={20} />
          </button>
        ) : (
          <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-display font-semibold">M</div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground leading-none">MaaPrint</p>
          <p className="font-display font-semibold text-sm truncate leading-tight">{shop?.shop_name || "Print Shop"}</p>
        </div>
        <Badge variant="outline" className={shop?.online ? "bg-brand-green/10 text-brand-green border-brand-green/30" : "text-muted-foreground"} data-testid="cs-online-badge">
          <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 ${shop?.online ? "bg-brand-green animate-pulse" : "bg-muted-foreground/50"}`} />
          {shop?.online ? "Online" : "Offline"}
        </Badge>
      </div>
    </header>
  );
}

function FullscreenLoader({ label }) {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-white dark:bg-neutral-950">
      <div className="flex flex-col items-center gap-2 text-muted-foreground">
        <Loader2 size={22} className="animate-spin text-primary" />
        <p className="text-sm">{label}</p>
      </div>
    </div>
  );
}

function ClosedShop({ error }) {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-white dark:bg-neutral-950 px-6" data-testid="cs-closed">
      <Card className="max-w-sm p-6 border-border text-center">
        <div className="h-14 w-14 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto">
          <X size={22} />
        </div>
        <h1 className="font-display font-semibold mt-4">Shop unavailable</h1>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/*                             LANDING                                   */
/* ═══════════════════════════════════════════════════════════════════ */

function Landing({ shop, onCamera, onGallery, onDocuments, onPassport }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const docRef = useRef(null);
  const psRef  = useRef(null);

  return (
    <div className="space-y-5" data-testid="cs-landing">
      <div className="mt-2">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
          Print in seconds
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Snap · Select · Pay via UPI · Auto-print at the shop.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Tile icon={Camera} tint="fuchsia-500"
          title="Camera" desc="Snap 1 or many pages · auto-crop"
          onClick={() => camRef.current?.click()} testid="cs-pick-camera" />
        <Tile icon={ImageIcon} tint="brand-green"
          title="Gallery" desc="Multiple JPG / PNG · auto-scan"
          onClick={() => galRef.current?.click()} testid="cs-pick-gallery" />
        <Tile icon={Files} tint="brand-blue"
          title="Documents" desc="Multiple PDFs · pages counted"
          onClick={() => docRef.current?.click()} testid="cs-pick-documents" />
        <Tile icon={UserSquare} tint="amber-500"
          title="Passport Photo" desc="8 copies · auto-fit · 4×6 glossy"
          onClick={() => psRef.current?.click()} testid="cs-pick-passport" />
      </div>

      {/* Hidden native inputs — `multiple` on all except passport. */}
      <input ref={camRef} type="file" accept="image/*" capture="environment" multiple
        className="hidden" data-testid="cs-file-camera"
        onChange={(e) => { onCamera(e.target.files); e.target.value = ""; }} />
      <input ref={galRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
        className="hidden" data-testid="cs-file-gallery"
        onChange={(e) => { onGallery(e.target.files); e.target.value = ""; }} />
      <input ref={docRef} type="file" accept="application/pdf" multiple
        className="hidden" data-testid="cs-file-documents"
        onChange={(e) => { onDocuments(e.target.files); e.target.value = ""; }} />
      <input ref={psRef} type="file" accept="image/jpeg,image/png,image/webp"
        className="hidden" data-testid="cs-file-passport"
        onChange={(e) => { onPassport(e.target.files?.[0]); e.target.value = ""; }} />

      {shop?.pricing && (
        <div className="rounded-xl bg-secondary/60 p-3 text-xs text-muted-foreground flex items-center gap-2" data-testid="cs-price-ribbon">
          <Package size={14} className="text-primary" />
          <span>B&amp;W {currency(shop.pricing.price_bw)}/page · Colour {currency(shop.pricing.price_color)}/page</span>
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, tint, title, desc, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-5 rounded-2xl border border-border bg-card hover:border-primary/60 hover:shadow-md active:scale-[0.98] transition-all text-left"
      data-testid={testid}
    >
      <div className={`h-14 w-14 shrink-0 rounded-xl bg-${tint}/10 text-${tint} flex items-center justify-center`}>
        <Icon size={26} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-display font-semibold text-lg">{title}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <div className="text-muted-foreground">›</div>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/*                              QUEUE                                    */
/* ═══════════════════════════════════════════════════════════════════ */

function QueueScreen({
  queue, processing, color, setColor,
  orientation, setOrientation,
  estCost, totalPages, pricing,
  paying,
  onRemove, onMove, onRotate, onPerspective,
  onAddCamera, onAddGallery, onAddDocuments,
  onPay, onCancel,
}) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const docRef = useRef(null);
  // A4 aspect (portrait: 8.27 : 11.69 → 210/297 ≈ 0.707; landscape → 1.414).
  const a4Aspect = orientation === "portrait" ? 210 / 297 : 297 / 210;

  return (
    <div className="space-y-4" data-testid="cs-queue">
      {/* Summary row */}
      <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3" data-testid="cs-queue-summary">
        <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Files size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-base leading-tight" data-testid="cs-queue-count">
            {queue.length} file{queue.length !== 1 ? "s" : ""} · {totalPages} page{totalPages !== 1 ? "s" : ""}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Auto-cropped, auto-enhanced · A4 {orientation === "portrait" ? "Portrait" : "Landscape"}
          </p>
        </div>
        {processing > 0 && (
          <div className="flex items-center gap-1.5 text-primary" data-testid="cs-queue-processing">
            <Loader2 size={14} className="animate-spin" />
            <span className="text-xs font-medium">{processing}</span>
          </div>
        )}
      </div>

      {/* Global orientation toggle — applied to the whole batch on upload. */}
      <div className="grid grid-cols-2 gap-2" data-testid="cs-orient-row">
        <button
          onClick={() => setOrientation("portrait")}
          className={`h-11 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 transition ${orientation === "portrait" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}
          data-testid="cs-orient-portrait"
        >
          <Rows3 size={14} /> Portrait
        </button>
        <button
          onClick={() => setOrientation("landscape")}
          className={`h-11 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 transition ${orientation === "landscape" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}
          data-testid="cs-orient-landscape"
        >
          <Columns3 size={14} /> Landscape
        </button>
      </div>

      {/* File list — each item = A4 print preview + per-file controls */}
      <ul className="space-y-3" data-testid="cs-queue-list">
        {queue.map((it, i) => (
          <li key={it.id} className="p-3 rounded-xl border border-border bg-card"
              data-testid={`cs-queue-item-${i}`}>
            {/* A4 print preview (accurate aspect for the chosen orientation) */}
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 rounded-md bg-neutral-100 dark:bg-neutral-900 border border-border overflow-hidden relative"
                style={{ width: 88, aspectRatio: `${a4Aspect}` }}
                data-testid={`cs-queue-a4-${i}`}
              >
                <div className="absolute inset-1 bg-white dark:bg-neutral-800 shadow-sm overflow-hidden rounded-sm flex items-center justify-center">
                  {it.processing ? (
                    <Loader2 size={14} className="animate-spin text-muted-foreground" />
                  ) : it.kind === "image" && it.previewUrl ? (
                    <img src={it.previewUrl} alt=""
                      className="w-full h-full object-contain"
                      data-testid={`cs-queue-preview-${i}`} />
                  ) : (
                    <div className="text-center px-1">
                      <FileText size={20} className="text-primary mx-auto" />
                      <p className="text-[9px] mt-0.5 text-muted-foreground leading-tight">
                        PDF<br />{it.pages}pg
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" title={it.name}>{it.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {it.processing ? "Working…"
                    : it.kind === "pdf" ? `PDF · ${it.pages} page${it.pages !== 1 ? "s" : ""}`
                    : "Image · 1 page"}
                  {it.autoEnhanced && !it.processing && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 text-brand-green">
                      <ScanSearch size={10} /> auto-scanned
                    </span>
                  )}
                </p>
                {/* Per-file controls (image only) */}
                {it.kind === "image" && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button
                      onClick={() => onRotate(it.id)}
                      disabled={it.processing}
                      className="h-8 px-2.5 rounded-md border border-border hover:border-primary/50 disabled:opacity-40 flex items-center gap-1 text-[11px] font-medium"
                      data-testid={`cs-item-rotate-${i}`}
                    >
                      <RotateCw size={11} /> Rotate
                    </button>
                    <button
                      onClick={() => onPerspective(it.id)}
                      disabled={it.processing}
                      className="h-8 px-2.5 rounded-md border border-border hover:border-primary/50 disabled:opacity-40 flex items-center gap-1 text-[11px] font-medium"
                      data-testid={`cs-item-perspective-${i}`}
                    >
                      <Scan size={11} /> Perspective
                    </button>
                  </div>
                )}
              </div>
              {/* Reorder + remove */}
              <div className="flex flex-col items-center gap-1.5">
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => onMove(it.id, -1)} disabled={i === 0}
                    className="h-6 w-6 rounded-md border border-border hover:border-primary/50 disabled:opacity-30 flex items-center justify-center"
                    data-testid={`cs-queue-up-${i}`}
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    onClick={() => onMove(it.id, +1)} disabled={i === queue.length - 1}
                    className="h-6 w-6 rounded-md border border-border hover:border-primary/50 disabled:opacity-30 flex items-center justify-center"
                    data-testid={`cs-queue-down-${i}`}
                  >
                    <ArrowDown size={12} />
                  </button>
                </div>
                <button
                  onClick={() => onRemove(it.id)}
                  className="h-8 w-8 rounded-lg text-destructive hover:bg-destructive/10 flex items-center justify-center"
                  data-testid={`cs-queue-remove-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Add more */}
      <div className="grid grid-cols-3 gap-2">
        <AddChip icon={Camera} label="Camera" onClick={() => camRef.current?.click()} testid="cs-add-camera" />
        <AddChip icon={ImageIcon} label="Gallery" onClick={() => galRef.current?.click()} testid="cs-add-gallery" />
        <AddChip icon={Files} label="Documents" onClick={() => docRef.current?.click()} testid="cs-add-documents" />
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
        onChange={(e) => { onAddCamera(e.target.files); e.target.value = ""; }}
        data-testid="cs-file-camera-more" />
      <input ref={galRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
        onChange={(e) => { onAddGallery(e.target.files); e.target.value = ""; }}
        data-testid="cs-file-gallery-more" />
      <input ref={docRef} type="file" accept="application/pdf" multiple className="hidden"
        onChange={(e) => { onAddDocuments(e.target.files); e.target.value = ""; }}
        data-testid="cs-file-documents-more" />

      {/* Colour toggle */}
      <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-card" data-testid="cs-queue-color-row">
        <div className="flex items-center gap-2">
          {color ? <Palette size={16} className="text-primary" /> : <Sun size={16} className="text-muted-foreground" />}
          <div>
            <p className="text-sm font-medium">{color ? "Colour print" : "Black & white"}</p>
            <p className="text-[11px] text-muted-foreground">
              {pricing
                ? `${currency(color ? pricing.price_color : pricing.price_bw)} / page`
                : ""}
            </p>
          </div>
        </div>
        <Switch checked={color} onCheckedChange={setColor} data-testid="cs-queue-color-switch" />
      </div>

      {/* Sticky pay bar — single tap → UPI */}
      <div className="fixed bottom-0 inset-x-0 z-20 bg-white/95 dark:bg-neutral-950/95 backdrop-blur-md border-t border-border">
        <div className="mx-auto max-w-md px-4 py-3">
          <button
            onClick={onPay}
            disabled={!queue.length || processing > 0 || paying}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl bg-primary hover:bg-brand-blue-hover text-white shadow-lg active:scale-[0.99] transition disabled:opacity-60"
            data-testid="cs-queue-pay"
          >
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-widest opacity-90 leading-none">
                {paying ? "Opening UPI…" : processing > 0 ? "Preparing…" : "Pay via UPI"}
              </p>
              <p className="font-display text-xl font-semibold leading-tight tabular-nums mt-0.5" data-testid="cs-queue-total">
                {currency(estCost)}
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center">
              {paying ? <Loader2 size={22} className="animate-spin" /> : <QrCode size={22} />}
            </div>
          </button>
          <p className="text-[10px] text-center text-muted-foreground mt-2 flex items-center justify-center gap-1">
            <ShieldCheck size={10} className="text-brand-green" />
            Secured by Razorpay · Auto-prints on payment
          </p>
        </div>
      </div>
    </div>
  );
}

function AddChip({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="h-14 rounded-xl border border-dashed border-border hover:border-primary/50 hover:bg-secondary/40 transition flex flex-col items-center justify-center gap-0.5 text-xs">
      <div className="flex items-center gap-1 text-primary">
        <Plus size={12} /> <Icon size={14} />
      </div>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/*                        PASSPORT EDITOR                                */
/* ═══════════════════════════════════════════════════════════════════ */

/** Untouched from iter 44 — single image → auto 8-copy 4×6 glossy layout. */
function PassportEditor({ job, slug, upload, setUpload, onContinue, paying }) {
  const [rotation, setRotation] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cacheBust, setCacheBust] = useState(Date.now());
  const kickedRef = useRef(false);

  useEffect(() => {
    if (upload || kickedRef.current || !job?.file) return;
    kickedRef.current = true;
    (async () => {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", job.file);
        fd.append("print_type", "passport");
        fd.append("passport_size", "IN_PASSPORT");
        fd.append("rotation_deg", "0");
        fd.append("copies", "1");
        fd.append("color", "true");
        fd.append("paper_size", "A4");
        fd.append("orientation", "landscape");
        fd.append("enhance_mode", "off");
        const { data } = await publicApi.post(`/shop/${slug}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUpload(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
      }
    })();
  }, [job, upload, slug, setUpload]);

  const previewUrl = upload?.preview_url
    ? `${BACKEND_URL}${upload.preview_url}?v=${cacheBust}` : null;

  const doRotate = async () => {
    if (!upload || busy) return;
    const next = (rotation + 90) % 360;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("rotation_deg", String(next));
      await publicApi.patch(
        `/shop/${slug}/passport-upload/${upload.upload_id}/rotation`, fd);
      setRotation(next);
      setCacheBust(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not rotate");
    } finally { setBusy(false); }
  };

  const cost = Number(upload?.cost || 0);

  return (
    <div className="space-y-4" data-testid="cs-passport">
      <div className="rounded-2xl border border-border bg-neutral-100 dark:bg-neutral-900 overflow-hidden shadow-inner">
        <div className="relative w-full flex items-center justify-center p-3" style={{ aspectRatio: "6 / 4" }}>
          <div className="relative h-full w-full bg-white dark:bg-neutral-800 shadow-md rounded-md overflow-hidden flex items-center justify-center">
            {previewUrl ? (
              <img src={previewUrl} alt="Passport 8-copy layout preview"
                className="absolute inset-0 w-full h-full object-contain"
                data-testid="cs-passport-preview" />
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Loader2 size={20} className="animate-spin text-primary" />
                <p className="text-sm">{uploading ? "Preparing 8-copy layout…" : "Waiting…"}</p>
              </div>
            )}
          </div>
        </div>
        <div className="px-4 py-2.5 border-t border-border text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Sparkles size={11} className="text-primary shrink-0" />
          <span>8 copies · 4×6" glossy · auto-fit · what you see prints</span>
        </div>
      </div>

      <button onClick={doRotate} disabled={!upload || busy || uploading}
        className="w-full flex items-center justify-center gap-2 h-12 rounded-xl border border-border bg-card hover:border-primary/50 transition text-sm font-medium disabled:opacity-50"
        data-testid="cs-passport-rotate">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
        <span>{busy ? "Rotating…" : "Rotate 90°"}</span>
      </button>

      <div className="fixed bottom-0 inset-x-0 z-20 bg-white/95 dark:bg-neutral-950/95 backdrop-blur-md border-t border-border">
        <div className="mx-auto max-w-md px-4 py-3">
          <button onClick={onContinue}
            disabled={!upload || busy || uploading || paying}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl bg-primary hover:bg-brand-blue-hover text-white shadow-lg active:scale-[0.99] transition disabled:opacity-60"
            data-testid="cs-passport-continue">
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-widest opacity-90 leading-none">
                {paying ? "Opening UPI…" : "Pay via UPI"}
              </p>
              <p className="font-display text-xl font-semibold leading-tight tabular-nums mt-0.5">
                {upload ? currency(cost) : "—"}
              </p>
            </div>
            <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center">
              {paying ? <Loader2 size={22} className="animate-spin" /> : <QrCode size={22} />}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/*                              TRACK                                    */
/* ═══════════════════════════════════════════════════════════════════ */

function TrackScreen({ orderInfo, trackStatus, shop, onNew }) {
  const status = trackStatus?.status || "pending";
  const nice = {
    pending: "Queued at the shop", printing: "Printing now",
    completed: "Ready to collect", awaiting_retailer_confirm: "Retailer verifying",
    cancelled: "Cancelled",
  }[status] || status;

  return (
    <div className="space-y-4" data-testid="cs-track">
      <div className="rounded-2xl border border-border bg-card p-5 text-center">
        <div className="h-14 w-14 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center mx-auto">
          <CheckCircle2 size={26} />
        </div>
        <h2 className="font-display text-2xl font-semibold mt-3">Payment received</h2>
        <p className="text-sm text-muted-foreground mt-1">Order at <b>{shop.shop_name}</b></p>
        <p className="text-[11px] text-muted-foreground mt-3 font-mono">#{orderInfo.order_id?.slice(0, 8) || "—"}</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <Clock3 size={16} className="text-primary" />
          <p className="text-sm font-medium">{nice}</p>
          {(status === "pending" || status === "printing") && <Loader2 className="ml-auto animate-spin text-muted-foreground" size={14} />}
        </div>
        {status === "completed" && (
          <p className="text-xs text-brand-green mt-2">Head to the counter to collect your prints.</p>
        )}
      </div>

      <Button onClick={onNew} className="w-full h-12 rounded-xl bg-primary hover:bg-brand-blue-hover" data-testid="cs-print-another">
        Print another
      </Button>
    </div>
  );
}
